import api, { getErrorMessage } from './api';

const workvivoService = {
  /**
   * Verify Workvivo access token
   * @param {object} data - Verification data
   * @param {string} data.access_token - Workvivo access token
   * @param {string} data.workvivo_base_url - Workvivo API base URL
   * @param {string} data.workvivo_id - Workvivo tenant ID
   * @returns {Promise<object>} - Verification result
   */
  verifyToken: async (data) => {
    try {
      const response = await api.post('/api/v1/workvivo/verify-token', data);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Complete Workvivo setup with import options
   * @param {object} data - Setup data
   * @param {string} data.access_token - Workvivo access token
   * @param {string} data.workvivo_base_url - Workvivo API base URL
   * @param {string} data.workvivo_id - Workvivo tenant ID
   * @param {string} data.organization_id - Optional existing organization ID
   * @param {string} data.organization_name - Organization name (for new org)
   * @param {string} data.organization_description - Organization description
   * @param {number} data.default_nomination_recipient_points - Default points for recipients
   * @param {number} data.default_nomination_sender_points - Default points for senders
   * @param {boolean} data.import_users - Whether to import users
   * @param {boolean} data.import_teams - Whether to import teams
   * @param {boolean} data.import_spaces - Whether to import spaces
   * @param {boolean} data.setup_bot - Whether to setup bot
   * @param {string} data.default_space_id - Default space ID
   * @returns {Promise<object>} - Setup result
   */
  completeSetup: async (data) => {
    try {
      const response = await api.post('/api/v1/workvivo/complete-setup', data);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get Workvivo setup status
   * @returns {Promise<object>} - Setup status
   */
  getSetupStatus: async () => {
    try {
      const response = await api.get('/api/v1/workvivo/setup-status');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * List Workvivo spaces
   * @returns {Promise<Array>} - Spaces list
   */
  getSpaces: async (orgId = null) => {
    try {
      const response = await api.get('/api/v1/workvivo/spaces', {
        params: orgId ? { org_id: orgId } : {},
      });
      return response.data?.data || [];
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * List Workvivo teams
   * @returns {Promise<Array>} - Teams list
   */
  getTeams: async () => {
    try {
      const response = await api.get('/api/v1/workvivo/teams');
      return response.data?.data || [];
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * List Workvivo users
   * @returns {Promise<Array>} - Users list
   */
  getUsers: async () => {
    try {
      const response = await api.get('/api/v1/workvivo/users');
      return response.data?.data || [];
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  // ---- Kudos / cross-post integration -----------------------------------

  /**
   * Lightweight check used by the recognition form to decide whether to
   * show the "Post to Workvivo" checkbox at all.
   */
  getRecognitionIntegrationStatus: async () => {
    try {
      const response = await api.get(
        '/api/v1/recognitions/workvivo/integration-status'
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * List Workvivo spaces where both the current user and the given recipient
   * are active members. Used to populate the space dropdown.
   */
  getPostableSpaces: async (recipientId) => {
    try {
      const response = await api.get('/api/v1/recognitions/workvivo/postable-spaces', {
        params: { recipient_id: recipientId },
      });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get the Workvivo kudo status for a given recognition.
   */
  getRecognitionStatus: async (recognitionId) => {
    try {
      const response = await api.get(`/api/v1/recognitions/${recognitionId}/workvivo`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Retry posting a recognition to Workvivo. Authorized to sender or org admin.
   */
  retryRecognition: async (recognitionId, spaceExternalIds = null) => {
    try {
      const body =
        spaceExternalIds !== null ? { space_external_ids: spaceExternalIds } : {};
      const response = await api.post(
        `/api/v1/recognitions/${recognitionId}/workvivo/retry`,
        body
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Delete the kudo from Workvivo for the given recognition (does NOT delete
   * the recognition itself in our app).
   */
  deleteRecognitionKudo: async (recognitionId) => {
    try {
      const response = await api.delete(
        `/api/v1/recognitions/${recognitionId}/workvivo`
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Admin: get the org's Workvivo integration summary including default_space_id.
   */
  getIntegration: async (orgId = null) => {
    try {
      const response = await api.get('/api/v1/workvivo/integration', {
        params: orgId ? { org_id: orgId } : {},
      });
      return response.data?.data || null;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Admin: update the org's Workvivo integration (currently default_space_id).
   */
  updateIntegration: async ({ default_space_id }, orgId = null) => {
    try {
      const response = await api.patch(
        '/api/v1/workvivo/integration',
        { default_space_id },
        { params: orgId ? { org_id: orgId } : {} },
      );
      return response.data?.data || null;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Admin: explain why the Important Dates column is empty — org toggles, what
   * the sync has written, and a live probe of the Workvivo users/events
   * endpoints. Returns counts and field names only, never personal data.
   */
  getImportantDatesDiagnostics: async (orgId = null) => {
    try {
      const response = await api.get(
        '/api/v1/workvivo/important-dates-diagnostics',
        { params: orgId ? { org_id: orgId } : {} }
      );
      return response.data?.data || null;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },
};

export default workvivoService;
